import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useCallback, useMemo } from 'react';
import { isCompleteGenerationId } from '../lib/generationIdInput';
import type { WithdrawalPaymentFilesFilters } from '../models';

const PAYMENT_FILES_QUERY_PARAMS_CONFIG = {
  payer_bank_id: { type: 'string' as const, default: '' },
  generation_id: { type: 'string' as const, default: '' },
  date_from: { type: 'string' as const, default: '' },
  date_to: { type: 'string' as const, default: '' },
};

const buildApiFilters = (queryParams: {
  payer_bank_id: string;
  generation_id: string;
  date_from: string;
  date_to: string;
}): WithdrawalPaymentFilesFilters => {
  const generationId = queryParams.generation_id.trim();

  return {
    payer_bank_id: queryParams.payer_bank_id ? Number(queryParams.payer_bank_id) : undefined,
    generation_id: generationId && isCompleteGenerationId(generationId) ? generationId : undefined,
    date_from: queryParams.date_from || undefined,
    date_to: queryParams.date_to || undefined,
  };
};

export const useWithdrawalPaymentFilesMain = () => {
  const { params: queryParams, updateParams } = useQueryParams(PAYMENT_FILES_QUERY_PARAMS_CONFIG);

  const apiFilters = useMemo(() => buildApiFilters(queryParams), [queryParams]);

  const filtersForUi = useMemo<WithdrawalPaymentFilesFilters>(
    () => ({
      payer_bank_id: apiFilters.payer_bank_id,
      generation_id: queryParams.generation_id || undefined,
      date_from: apiFilters.date_from,
      date_to: apiFilters.date_to,
    }),
    [apiFilters, queryParams.generation_id],
  );

  const handleFiltersChange = useCallback(
    (partial: Partial<WithdrawalPaymentFilesFilters>) => {
      const updates: Partial<typeof queryParams> = {};

      if ('payer_bank_id' in partial) {
        updates.payer_bank_id = partial.payer_bank_id ? String(partial.payer_bank_id) : '';
      }
      if ('generation_id' in partial) {
        updates.generation_id = partial.generation_id ?? '';
      }
      if ('date_from' in partial) {
        updates.date_from = partial.date_from ?? '';
      }
      if ('date_to' in partial) {
        updates.date_to = partial.date_to ?? '';
      }

      updateParams(updates);
    },
    [updateParams],
  );

  const hasActiveFilters = Boolean(
    apiFilters.payer_bank_id || queryParams.generation_id.trim() || apiFilters.date_from || apiFilters.date_to,
  );

  return {
    apiFilters,
    filtersForUi,
    handleFiltersChange,
    hasActiveFilters,
  };
};
